(function () {
    "use strict";

    const SECURITY_INCIDENTS = "SECURITY_INCIDENTS";

    angular.module("app").factory("securityIncidentsService", SecurityIncidentsService);

    SecurityIncidentsService.$inject = [
        "$rootScope", "$filter", "$timeout",
        "habilBootbox", "habilEnabledFeatures", "notificationService", "settingsService",
        "securityIncidentsApi",
    ];

    function SecurityIncidentsService(
        $rootScope, $filter, $timeout,
        habilBootbox, habilEnabledFeatures, notificationService, settingsService,
        securityIncidentsApi,
    ) {
        const service = {
            get isFeatureEnabled() {
                return habilEnabledFeatures.includes(SECURITY_INCIDENTS);
            },
            showCreateIncidentModal,
        };

        const getTranslation = $filter("habilTranslation");
        const showSuccessMessage = (message) => notificationService.success(message);
        const showErrorMessage = (message) => notificationService.error(message);

        return service;

        function showCreateIncidentModal(request, { lockInvolvedPersons = false } = {}) {
            if (!service.isFeatureEnabled) {
                return Promise.resolve(false);
            }

            return new Promise((resolve) => {
                const $creationScope = $rootScope.$new(true);

                const newSecurityIncident = {
                    Description: "",
                    IsSecuredOrAdministrativeZoneInvolved: false,
                    IsClassifiedInformationInvolved: false,
                    IsCisInvolved: false,
                    IsPersonWithClearanceInvolved: false,
                    IsSecurityInfrastructureDamaged: false,
                    Info: {
                        involvedPersons: [],
                        incidentManagement: {
                            damageAnalysis: "",
                        },
                        installations: [],
                    }
                };

                $creationScope.SecurityIncident = newSecurityIncident;
                $creationScope.languageCode = settingsService.locale.toLowerCase();
                $creationScope.lockInvolvedPersons = lockInvolvedPersons;

                const currentDate = new Date();
                $creationScope.today = currentDate.toISOString();
                $creationScope.oneYearAgo = (currentDate.setFullYear(currentDate.getFullYear() - 1) && currentDate).toISOString();

                $creationScope.handleInvolvedSelectionChanged = handleInvolvedSelectionChanged;
                $creationScope.getPersonDisplayValue = getPersonDisplayValue;

                $creationScope.handleInstallationsSelectionChanged = handleInstallationsSelectionChanged;
                $creationScope.getInstallationDisplayValue = getInstallationDisplayValue;
                $creationScope.lockInstallations = false;

                $creationScope.wizard = new habilCommon.Wizard([
                    new habilCommon.Step(
                        getTranslation("SecurityIncident_Description_Title"),
                        "si-description",
                        {
                            isRequiredCallback: habilCommon.alwaysTrue,
                            info: getInfoMessage("SecurityIncident_Report_Incident_Info"),
                            isValidCallback: () => {
                                const incident = $creationScope.SecurityIncident;

                                return !!(incident.Location?.trim().length && incident.DateTime && incident.Description?.trim().length);
                            },
                            onBeforeFinish: (step) => {
                                if (!step.isValid) {
                                    showErrorMessage(getTranslation("GetMissingFieldsError"));

                                    return false;
                                }
                            },
                        },
                    ),
                    new habilCommon.Step(
                        getTranslation("SecurityIncident_Damage_Title"),
                        "si-damage",
                    ),
                    new habilCommon.Step(
                        getTranslation("SecurityIncident_InvolvedPersons"),
                        "si-involved",
                        {
                            isRequiredCallback: () => {
                                return $creationScope.SecurityIncident.IsPersonWithClearanceInvolved;
                            },
                            isValidCallback: () => {
                                const incident = $creationScope.SecurityIncident;

                                if (incident.IsPersonWithClearanceInvolved) {
                                    return !!incident.InvolvedPersons?.length || !!incident.Info.involvedPersons?.length;
                                }

                                return true;
                            },
                            onBeforeFinish: (step) => {
                                if (!step.isValid) {
                                    showErrorMessage(getTranslation("GetMissingFieldsError"));

                                    return false;
                                }
                            },
                        },
                    ),
                    new habilCommon.Step(
                        getTranslation("SecurityIncident_PhysicalInstallations"),
                        "si-zone",
                        {
                            info: getInfoMessage("SecurityIncident_PhysicalInstallations_Info"),
                            isEnabledCallback: () => {
                                if (!$creationScope.SecurityIncident) {
                                    return false;
                                }

                                return $creationScope.SecurityIncident.IsSecuredOrAdministrativeZoneInvolved || $creationScope.SecurityIncident.IsSecurityInfrastructureDamaged;
                            },
                            isValidCallback: () => {
                                const incident = $creationScope.SecurityIncident;

                                if (incident.IsSecuredOrAdministrativeZoneInvolved) {
                                    return !!incident.Info.installations?.length;
                                }

                                return true;
                            },
                            onBeforeFinish: (step) => {
                                if (!step.isValid) {
                                    showErrorMessage(getTranslation("GetMissingFieldsError"));

                                    return false;
                                }
                            },
                        },
                    ),
                    new habilCommon.Step(
                        getTranslation("Common_Overview_Label"),
                        "si-overview",
                        {
                            actions: [
                                new habilCommon.Action(getTranslation("Button_Confirm"), "btn-primary", async () => {
                                    if (await confirmCallback()) {
                                        habilBootbox.hideAll();
                                    }
                                }),
                            ],
                        }
                    )
                ]);

                newSecurityIncident.InvolvedPersons = getInvolvedPersons(request);

                let isCreatingIncident = false;

                $creationScope.closeModal = () => {
                    habilBootbox.hideAll();
                };

                $creationScope.newOtherInvolvedPerson = {};

                $creationScope.addOtherInvolvedPerson = () => {
                    const person = $creationScope.newOtherInvolvedPerson;

                    if (!person.name && !person.firstName) {
                        return;
                    }

                    const nameLength =
                        + (person.name?.trim().length ?? 0)
                        + (person.firstName?.trim().length ?? 0);

                    if (nameLength === 0) {
                        return;
                    }

                    newSecurityIncident.Info.involvedPersons.push($creationScope.newOtherInvolvedPerson);

                    // Reset fields
                    $creationScope.newOtherInvolvedPerson = {};

                    return true;
                };

                $creationScope.deleteOtherInvolvedPerson = (index) => {
                    newSecurityIncident.Info.involvedPersons.splice(index, 1);
                };

                const confirmCallback = () => {
                    isCreatingIncident = true;

                    return securityIncidentsApi.Create(
                        {
                            SecurityIncident: { ...newSecurityIncident, InvolvedPersons: newSecurityIncident.InvolvedPersons.map(i => i.DossierOrganisationPersonPublicId) },
                        },
                        () => {
                            showSuccessMessage(getTranslation("SecurityIncident_Created"));
                            resolve(true);

                            return true;
                        },
                        (error) => {
                            const message = error.data?.message || typeof error.data === "string" ? error.data : getTranslation("Unexpected_Error_Message");
                            showErrorMessage(message);

                            return false;
                        },
                        () => { $creationScope.SecurityIncident = null; }
                    );
                };

                habilBootbox.customDialog({
                    templateUrl: "@/security-incidents/create.html",
                    scope: $creationScope,
                    size: "extra-large",
                    onEscape: false,
                    closeButton: true,
                    title: getTranslation("Report_SecurityIncident"),
                    buttons: {},
                    onHidden: () => {
                        // When we are not in the process of creating an incident, the user clicked Cancel or closed the modal (X).
                        if (!isCreatingIncident) {
                            resolve(false);
                        }

                        // Cleanup the scope when the modal closes.
                        $creationScope.$destroy();
                    }
                });

                function getInfoMessage(translationKey) {
                    return getTranslation(translationKey);
                }

                function handleInvolvedSelectionChanged(selection) {
                    $timeout(() => {
                        newSecurityIncident.InvolvedPersons = selection;
                    });
                }

                function handleInstallationsSelectionChanged(selection) {
                    $timeout(() => {
                        newSecurityIncident.Info.installations = selection;
                    });
                }
            });

            function getInvolvedPersons(request) {
                return request ?
                    [
                        {
                            FullName: getFullName(request),
                            ReferenceNbr: request.ReferenceNumber,
                            DossierNbr: request.DossierNumber ?? request.PersonDossierId,
                            DossierOrganisationPersonPublicId: request.DossierOrganisationPersonPublicId,
                            // Block removal by adding a flag here?
                        }
                    ] : [];
            }

            function getFullName(request) {
                const first = request.Firstnames ?? request.Firstname ?? "";

                return `${first.length > 0 ? `${first} ` : ""}${request.Name}`;
            }

            function getPersonDisplayValue(item) {
                return item.FullName;
            }

            function getInstallationDisplayValue(item) {
                return `${item.identifier} (${item.zones.join(", ")})`;
            }
        }
    }
})();